import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { CONTROL_BASE, CONTROL_SIZES } from './controlStyles';

export interface SelectOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  onBlur?: () => void;
  className?: string;
  /** Show the search field once there are more options than this. */
  searchThreshold?: number;
}

const PANEL_MAX_HEIGHT = 288;

/** Dropdown with the look of a boxed input: shows the chosen label, opens a scrollable, searchable panel. Escapes any clipping ancestor via a portal. */
export function Combobox({ options, value, onChange, placeholder = 'Select…', disabled, id, onBlur, className, searchThreshold = 8 }: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [panelStyle, setPanelStyle] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selected = options.find((o) => o.value === value);
  const showSearch = options.length > searchThreshold;
  const filtered = query.trim() ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())) : options;

  useEffect(() => {
    if (!open) return;

    function place() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
      setPanelStyle(
        openUpward
          ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
          : { top: rect.bottom + 4, left: rect.left, width: rect.width },
      );
    }

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(Math.max(0, options.findIndex((o) => o.value === value)));
    const frame = requestAnimationFrame(() => (showSearch ? searchRef.current : panelRef.current)?.focus());
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function close() {
    setOpen(false);
    onBlur?.();
  }

  function commit(v: string) {
    onChange(v);
    close();
    triggerRef.current?.focus();
  }

  function handleKeyDown(event: ReactKeyboardEvent) {
    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[highlight];
      if (option) commit(option.value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      triggerRef.current?.focus();
    } else if (event.key === 'Tab') {
      // Let focus move on to the next field naturally; just close the panel first.
      close();
    }
  }

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        className={cn(
          CONTROL_BASE,
          CONTROL_SIZES.md,
          'flex w-full min-w-[200px] items-center justify-between gap-2 text-left font-medium',
          disabled && 'cursor-not-allowed opacity-60',
        )}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
      >
        <span className={cn('truncate', !selected && 'font-normal text-fg-muted')}>{selected ? selected.label : placeholder}</span>
        <ChevronDown size={15} className={cn('flex-none text-fg-muted transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            tabIndex={-1}
            style={{ position: 'fixed', ...panelStyle }}
            className="z-50 flex flex-col overflow-hidden rounded-lg border border-line-strong bg-surface shadow-popover"
          >
            {showSearch && (
              <div className="flex flex-none items-center gap-2 border-b border-line px-3 py-2">
                <Search size={14} className="flex-none text-fg-muted" aria-hidden />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search…"
                  className="w-full bg-transparent text-[13px] outline-none placeholder:text-fg-muted"
                />
              </div>
            )}
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              tabIndex={showSearch ? undefined : -1}
              onKeyDown={handleKeyDown}
              className="overflow-y-auto py-1"
              style={{ maxHeight: PANEL_MAX_HEIGHT }}
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-[13px] text-fg-muted">No matches.</li>
              ) : (
                filtered.map((option, index) => (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => commit(option.value)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-[13px]',
                      index === highlight && 'bg-accent-wash',
                      option.value === value && 'font-medium text-accent',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.value === value && <Check size={14} className="flex-none text-accent" aria-hidden />}
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
